/**
 * API Error Types and Classes
 *
 * Comprehensive error handling types and classes for the Personal Trainer App API.
 * Provides standardized error structures, custom error classes, and utilities
 * for consistent error handling across the application.
 *
 * Features:
 * - Custom error classes for different error types
 * - Standardized error response formats
 * - Error severity levels and categorization
 * - Error code mapping and user-friendly messages
 * - Validation error handling for Laravel responses
 * - Network and timeout error handling
 * - Error reporting and logging structures
 */

import { HTTP_STATUS } from '../../config/constants';
import { dayjs } from '../../utils/date';
import { PASSWORD_MIN_LENGTH } from '../../validation/auth.schemas';

import type { ConfirmationMethod, SubscriptionTier } from '../auth';

// =============================================================================
// BASE ERROR INTERFACES
// =============================================================================

/**
 * Base API error interface
 */
export interface BaseApiError {
  message: string;
  code: string;
  timestamp: string;
  details?: Record<string, unknown>;
}

/**
 * Extended API error with HTTP context
 */
export interface ApiError extends BaseApiError {
  status?: number;
  statusText?: string;
  url?: string;
  method?: string;
  requestId?: string;
  /** When the request was first sent (epoch ms); see `isRefusalFromEarlierView`. */
  requestStartedAt?: number;
  correlationId?: string;
}

/**
 * Validation error details from Laravel
 */
export interface ValidationErrorInfo {
  field: string;
  message: string;
  rule: string;
  value?: unknown;
}

/**
 * API error response structure
 */
export interface ApiErrorResponse {
  message: string;
  errors?: Record<string, string[]>;
  code?: string;
  status?: number;
  details?: Record<string, unknown>;
  /**
   * Feature-agnostic envelope, e.g. the 402 paywall payload. `reason` says
   * why a refusal happened where one code covers several (the session stop /
   * cancel refusals); branch on `code` first, then on it.
   */
  meta?: Record<string, unknown> & { reason?: string };
  trace?: string; // Only in development
}

// =============================================================================
// ERROR SEVERITY AND CATEGORIES
// =============================================================================

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical',
}

/**
 * Error categories for classification
 */
export enum ErrorCategory {
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NETWORK = 'network',
  SERVER = 'server',
  CLIENT = 'client',
  TIMEOUT = 'timeout',
  RATE_LIMIT = 'rate_limit',
  MAINTENANCE = 'maintenance',
  UNKNOWN = 'unknown',
}

/**
 * Error types with metadata
 */
export interface ErrorTypeInfo {
  category: ErrorCategory;
  severity: ErrorSeverity;
  retryable: boolean;
  userMessage: string;
  actionable: boolean;
  reportable: boolean;
}

// =============================================================================
// ERROR CODE DEFINITIONS
// =============================================================================

/**
 * Comprehensive error codes
 */
export const API_ERROR_CODES = {
  // Authentication errors
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  TOKEN_INVALID: 'TOKEN_INVALID',
  TOKEN_MISSING: 'TOKEN_MISSING',
  REFRESH_TOKEN_EXPIRED: 'REFRESH_TOKEN_EXPIRED',
  REFRESH_TOKEN_INVALID: 'REFRESH_TOKEN_INVALID',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_LOCKED: 'ACCOUNT_LOCKED',
  SESSION_ENDED: 'SESSION_ENDED',
  EMAIL_NOT_VERIFIED: 'EMAIL_NOT_VERIFIED',
  TWO_FACTOR_REQUIRED: 'TWO_FACTOR_REQUIRED',

  // Authorization errors
  FORBIDDEN: 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  RESOURCE_FORBIDDEN: 'RESOURCE_FORBIDDEN',
  PAYMENT_REQUIRED: 'PAYMENT_REQUIRED',
  /**
   * The action needs the owner to confirm it's them, recently (HTTP 423). The
   * session is fine; `meta` says which methods can confirm. Confirm, then retry
   * the request.
   */
  CONFIRMATION_REQUIRED: 'CONFIRMATION_REQUIRED',
  /**
   * An admin is signed in as this member from the admin panel, and the session
   * is read-only (HTTP 403). Every write is refused with it, and so are the
   * reads that act (billing portal, exports). The `message` is the one to show.
   */
  READ_ONLY: 'READ_ONLY',

  // Validation errors
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  INVALID_INPUT: 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
  INVALID_FORMAT: 'INVALID_FORMAT',
  VALUE_TOO_LONG: 'VALUE_TOO_LONG',
  VALUE_TOO_SHORT: 'VALUE_TOO_SHORT',
  INVALID_EMAIL: 'INVALID_EMAIL',
  WEAK_PASSWORD: 'WEAK_PASSWORD',
  PASSWORD_MISMATCH: 'PASSWORD_MISMATCH',

  // Resource errors
  NOT_FOUND: 'NOT_FOUND',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  RESOURCE_ALREADY_EXISTS: 'RESOURCE_ALREADY_EXISTS',
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
  /**
   * You acted on a version that has since moved, and the response carries what
   * moved. A sibling of RESOURCE_CONFLICT, not a replacement: a conflict is a
   * refusal, this is a refusal plus a diff to re-decide against.
   *
   * **Never inferred from a status.** A 409 maps to RESOURCE_CONFLICT, because
   * staleness is a claim about a specific read having gone out of date, which
   * only the raising endpoint knows. This code arrives when the body says so.
   */
  RESOURCE_STALE: 'RESOURCE_STALE',
  RESOURCE_DELETED: 'RESOURCE_DELETED',

  // Server errors
  SERVER_ERROR: 'SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  BAD_GATEWAY: 'BAD_GATEWAY',
  GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',
  DATABASE_ERROR: 'DATABASE_ERROR',
  THIRD_PARTY_SERVICE_ERROR: 'THIRD_PARTY_SERVICE_ERROR',

  // Network errors
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT_ERROR: 'TIMEOUT_ERROR',
  CONNECTION_ERROR: 'CONNECTION_ERROR',
  DNS_ERROR: 'DNS_ERROR',
  SSL_ERROR: 'SSL_ERROR',

  // Rate limiting
  RATE_LIMITED: 'RATE_LIMITED',
  TOO_MANY_REQUESTS: 'TOO_MANY_REQUESTS',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',

  // Maintenance
  MAINTENANCE_MODE: 'MAINTENANCE_MODE',
  SERVICE_DEGRADED: 'SERVICE_DEGRADED',

  // Client errors
  BAD_REQUEST: 'BAD_REQUEST',
  INVALID_JSON: 'INVALID_JSON',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',

  // Unknown
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
} as const;

export type ApiErrorCode =
  (typeof API_ERROR_CODES)[keyof typeof API_ERROR_CODES];

// =============================================================================
// ERROR TYPE MAPPING
// =============================================================================

/**
 * Error code to type info mapping
 */
export const ERROR_TYPE_MAP: Record<ApiErrorCode, ErrorTypeInfo> = {
  // Authentication errors
  [API_ERROR_CODES.UNAUTHENTICATED]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.HIGH,
    retryable: false,
    userMessage: 'Your session has expired. Please log in again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.TOKEN_EXPIRED]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    retryable: true,
    userMessage: 'Your session has expired. Please log in again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.SESSION_ENDED]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Your session ended before this finished.',
    actionable: false,
    reportable: false,
  },
  [API_ERROR_CODES.TOKEN_INVALID]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.HIGH,
    retryable: false,
    userMessage: 'Authentication failed. Please log in again.',
    actionable: true,
    reportable: true,
  },
  [API_ERROR_CODES.INVALID_CREDENTIALS]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Invalid email or password. Please try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.ACCOUNT_LOCKED]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.HIGH,
    retryable: false,
    userMessage: 'Your account has been locked. Please contact support.',
    actionable: true,
    reportable: false,
  },
  // A 403, not a 401: the account is signed in, and refused until its address
  // is verified, so it types with the status the API sent.
  [API_ERROR_CODES.EMAIL_NOT_VERIFIED]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage: 'Please verify your email address before continuing.',
    actionable: true,
    reportable: false,
  },

  // Authorization errors
  [API_ERROR_CODES.FORBIDDEN]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage: 'You do not have permission to access this resource.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.INSUFFICIENT_PERMISSIONS]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage: 'You do not have sufficient permissions for this action.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.PAYMENT_REQUIRED]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'This feature requires a premium subscription.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.CONFIRMATION_REQUIRED]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.LOW,
    // The same request succeeds once the owner has confirmed, and not before.
    retryable: false,
    userMessage: "Confirm it's you to continue.",
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.READ_ONLY]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.LOW,
    // Refused for as long as the admin is signed in as the member.
    retryable: false,
    userMessage:
      "Read-only: you can't make changes while signed in from the admin panel.",
    actionable: false,
    // An expected refusal, not a fault: nothing for Sentry.
    reportable: false,
  },

  // Validation errors
  [API_ERROR_CODES.VALIDATION_ERROR]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Please check your input and try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.INVALID_INPUT]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'The provided data is invalid. Please check and try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.INVALID_EMAIL]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Please enter a valid email address.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.WEAK_PASSWORD]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: `Password must be at least ${PASSWORD_MIN_LENGTH} characters, include a letter, and not have appeared in a data leak.`,
    actionable: true,
    reportable: false,
  },

  // Resource errors
  [API_ERROR_CODES.NOT_FOUND]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'The requested resource was not found.',
    actionable: false,
    reportable: false,
  },
  [API_ERROR_CODES.RESOURCE_NOT_FOUND]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'The requested item could not be found.',
    actionable: false,
    reportable: false,
  },
  [API_ERROR_CODES.RESOURCE_CONFLICT]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage:
      'This action conflicts with the current state. Please refresh and try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.RESOURCE_STALE]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.MEDIUM,
    // Retrying the same request unchanged gets the same refusal — what makes it
    // succeed is a decision the caller has not made yet.
    retryable: false,
    userMessage: 'This has changed since you last read it.',
    actionable: true,
    reportable: false,
  },

  // Server errors
  [API_ERROR_CODES.SERVER_ERROR]: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    retryable: true,
    userMessage: 'Something went wrong on our end. Please try again later.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.SERVICE_UNAVAILABLE]: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    retryable: true,
    userMessage: 'Service is temporarily unavailable. Please try again later.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.BAD_GATEWAY]: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    retryable: true,
    userMessage: 'Service is temporarily unavailable. Please try again later.',
    actionable: false,
    reportable: true,
  },

  // Network errors
  [API_ERROR_CODES.NETWORK_ERROR]: {
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.MEDIUM,
    retryable: true,
    userMessage: 'Network error. Please check your connection and try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.TIMEOUT_ERROR]: {
    category: ErrorCategory.TIMEOUT,
    severity: ErrorSeverity.MEDIUM,
    retryable: true,
    userMessage:
      'Request timed out. Please check your connection and try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.CONNECTION_ERROR]: {
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.MEDIUM,
    retryable: true,
    userMessage: 'Connection failed. Please check your internet connection.',
    actionable: true,
    reportable: false,
  },

  // Rate limiting
  [API_ERROR_CODES.RATE_LIMITED]: {
    category: ErrorCategory.RATE_LIMIT,
    severity: ErrorSeverity.LOW,
    retryable: true,
    userMessage:
      'You are making requests too quickly. Please wait and try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.TOO_MANY_REQUESTS]: {
    category: ErrorCategory.RATE_LIMIT,
    severity: ErrorSeverity.LOW,
    retryable: true,
    userMessage: 'Too many requests. Please wait a moment before trying again.',
    actionable: true,
    reportable: false,
  },

  // Maintenance
  [API_ERROR_CODES.MAINTENANCE_MODE]: {
    category: ErrorCategory.MAINTENANCE,
    severity: ErrorSeverity.MEDIUM,
    retryable: true,
    userMessage:
      'The service is currently under maintenance. Please try again later.',
    actionable: false,
    reportable: false,
  },

  // Client errors
  [API_ERROR_CODES.BAD_REQUEST]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Invalid request. Please check your input and try again.',
    actionable: true,
    reportable: true,
  },
  [API_ERROR_CODES.PAYLOAD_TOO_LARGE]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage:
      'The uploaded file is too large. Please choose a smaller file.',
    actionable: true,
    reportable: false,
  },

  // Default mappings for codes not explicitly defined above
  [API_ERROR_CODES.TOKEN_MISSING]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage: 'Authentication required. Please log in.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.REFRESH_TOKEN_EXPIRED]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage: 'Session expired. Please log in again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.REFRESH_TOKEN_INVALID]: {
    category: ErrorCategory.AUTHENTICATION,
    severity: ErrorSeverity.HIGH,
    retryable: false,
    userMessage: 'Authentication failed. Please log in again.',
    actionable: true,
    reportable: true,
  },
  // A 403, not a 401: the account is signed in, and refused an action (adding
  // progress photos) until two-factor is on. Branch on the code, not the
  // status: FORBIDDEN is a 403 too.
  [API_ERROR_CODES.TWO_FACTOR_REQUIRED]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Two-factor authentication required.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.RESOURCE_FORBIDDEN]: {
    category: ErrorCategory.AUTHORIZATION,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage: 'Access to this resource is forbidden.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.MISSING_REQUIRED_FIELD]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage:
      'Required fields are missing. Please complete all required fields.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.INVALID_FORMAT]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Invalid format. Please check your input.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.VALUE_TOO_LONG]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Input is too long. Please shorten and try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.VALUE_TOO_SHORT]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Input is too short. Please provide more information.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.PASSWORD_MISMATCH]: {
    category: ErrorCategory.VALIDATION,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Passwords do not match. Please try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.RESOURCE_ALREADY_EXISTS]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'This item already exists.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.RESOURCE_DELETED]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'This resource has been deleted.',
    actionable: false,
    reportable: false,
  },
  [API_ERROR_CODES.GATEWAY_TIMEOUT]: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    retryable: true,
    userMessage: 'Request timed out. Please try again.',
    actionable: true,
    reportable: true,
  },
  [API_ERROR_CODES.DATABASE_ERROR]: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.CRITICAL,
    retryable: true,
    userMessage: 'Database error. Please try again later.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.THIRD_PARTY_SERVICE_ERROR]: {
    category: ErrorCategory.SERVER,
    severity: ErrorSeverity.HIGH,
    retryable: true,
    userMessage: 'External service error. Please try again later.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.DNS_ERROR]: {
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.MEDIUM,
    retryable: true,
    userMessage: 'Network configuration error. Please try again.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.SSL_ERROR]: {
    category: ErrorCategory.NETWORK,
    severity: ErrorSeverity.HIGH,
    retryable: false,
    userMessage: 'Secure connection error. Please contact support.',
    actionable: false,
    reportable: true,
  },
  [API_ERROR_CODES.QUOTA_EXCEEDED]: {
    category: ErrorCategory.RATE_LIMIT,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage:
      'Usage quota exceeded. Please upgrade your plan or try again later.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.SERVICE_DEGRADED]: {
    category: ErrorCategory.MAINTENANCE,
    severity: ErrorSeverity.LOW,
    retryable: true,
    userMessage: 'Service is running slowly. Some features may be limited.',
    actionable: false,
    reportable: false,
  },
  [API_ERROR_CODES.INVALID_JSON]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.MEDIUM,
    retryable: false,
    userMessage: 'Invalid data format. Please try again.',
    actionable: true,
    reportable: true,
  },
  [API_ERROR_CODES.UNSUPPORTED_MEDIA_TYPE]: {
    category: ErrorCategory.CLIENT,
    severity: ErrorSeverity.LOW,
    retryable: false,
    userMessage: 'Unsupported file type. Please choose a different file.',
    actionable: true,
    reportable: false,
  },
  [API_ERROR_CODES.UNKNOWN_ERROR]: {
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.MEDIUM,
    retryable: true,
    userMessage: 'An unexpected error occurred. Please try again.',
    actionable: true,
    reportable: true,
  },
  [API_ERROR_CODES.UNEXPECTED_ERROR]: {
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.HIGH,
    retryable: false,
    userMessage:
      'An unexpected error occurred. Please contact support if this continues.',
    actionable: false,
    reportable: true,
  },
};

// =============================================================================
// CUSTOM ERROR CLASSES
// =============================================================================

/**
 * Base API Error class
 */
export class ApiErrorClass extends Error implements ApiError {
  public readonly code: string;
  public readonly status?: number;
  public readonly statusText?: string;
  public readonly url?: string;
  public readonly method?: string;
  public readonly requestId?: string;
  public readonly requestStartedAt?: number;
  public readonly correlationId?: string;
  public readonly timestamp: string;
  public readonly details?: Record<string, unknown>;
  public readonly category: ErrorCategory;
  public readonly severity: ErrorSeverity;
  public readonly retryable: boolean;
  public readonly actionable: boolean;
  public readonly reportable: boolean;

  constructor(
    message: string,
    code: ApiErrorCode = API_ERROR_CODES.UNKNOWN_ERROR,
    status?: number,
    details?: Record<string, unknown>
  ) {
    super(message);

    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.timestamp = dayjs().toISOString();
    this.details = details;

    // Get error type info
    const typeInfo =
      ERROR_TYPE_MAP[code] || ERROR_TYPE_MAP[API_ERROR_CODES.UNKNOWN_ERROR];
    this.category = typeInfo.category;
    this.severity = typeInfo.severity;
    this.retryable = typeInfo.retryable;
    this.actionable = typeInfo.actionable;
    this.reportable = typeInfo.reportable;

    // Ensure proper prototype chain
    Object.setPrototypeOf(this, ApiErrorClass.prototype);
  }

  /**
   * Get user-friendly error message
   */
  getUserMessage(): string {
    const typeInfo = ERROR_TYPE_MAP[this.code as ApiErrorCode];
    return typeInfo?.userMessage || this.message;
  }

  /**
   * Convert to plain object for serialization
   */
  toJSON(): ApiError {
    return {
      message: this.message,
      code: this.code,
      status: this.status,
      statusText: this.statusText,
      url: this.url,
      method: this.method,
      requestId: this.requestId,
      correlationId: this.correlationId,
      timestamp: this.timestamp,
      details: this.details,
    };
  }
}

/**
 * Authentication Error class
 */
export class AuthError extends ApiErrorClass {
  constructor(
    message: string,
    code: ApiErrorCode = API_ERROR_CODES.UNAUTHENTICATED,
    details?: Record<string, unknown>
  ) {
    super(message, code, HTTP_STATUS.UNAUTHORIZED, details);
    this.name = 'AuthError';
    Object.setPrototypeOf(this, AuthError.prototype);
  }
}

/**
 * Validation Error class
 */
export class ValidationErrorClass extends ApiErrorClass {
  public readonly validationErrors: Record<string, string[]>;

  constructor(
    message: string,
    validationErrors: Record<string, string[]> = {},
    details?: Record<string, unknown>
  ) {
    super(
      message,
      API_ERROR_CODES.VALIDATION_ERROR,
      HTTP_STATUS.UNPROCESSABLE_ENTITY,
      {
        ...details,
        validationErrors,
      }
    );

    this.name = 'ValidationError';
    this.validationErrors = validationErrors;
    Object.setPrototypeOf(this, ValidationErrorClass.prototype);
  }

  /**
   * Get validation errors for a specific field
   */
  getFieldErrors(field: string): string[] {
    return this.validationErrors[field] || [];
  }

  /**
   * Check if field has validation errors
   */
  hasFieldError(field: string): boolean {
    return (
      field in this.validationErrors && this.validationErrors[field].length > 0
    );
  }

  /**
   * Get all validation errors as a flat array
   */
  getAllErrors(): ValidationErrorInfo[] {
    const errors: ValidationErrorInfo[] = [];

    for (const [field, messages] of Object.entries(this.validationErrors)) {
      for (const message of messages) {
        errors.push({
          field,
          message,
          rule: 'unknown',
          value: (this.details?.originalData as Record<string, unknown>)?.[
            field
          ],
        });
      }
    }

    return errors;
  }
}

// =============================================================================
// PAYWALL (402) TYPES
// =============================================================================

/**
 * Ready-to-render upgrade copy authored by the backend (`meta.upgrade_prompt`).
 * The client renders this verbatim; it authors no per-feature copy.
 */
export interface UpgradePrompt {
  title: string;
  message: string;
  cta: string;
  target_plan: SubscriptionTier; // enum value ("pro"), NOT the display name
  benefits: string[];
}

/**
 * The gated feature descriptor (`meta.feature`). `slug` is a stable identifier
 * for analytics/keying and future proactive gating.
 */
export interface PaywallFeature {
  slug: string;
  name: string;
  description?: string;
  category?: string;
  benefits?: string[];
}

/**
 * Normalized paywall payload stamped onto `details.paywall` by
 * `transformAxiosToApiError` when it parses a 402 `meta` envelope. Every field
 * is optional so a bare 402 (no meta) still types cleanly.
 */
export interface PaywallDetails {
  requiredTier?: SubscriptionTier; // from upgrade_prompt.target_plan
  currentTier?: SubscriptionTier; // from subscription.current (via tierFromPlanName)
  upgradePrompt?: UpgradePrompt;
  feature?: PaywallFeature;
  upgradeUrl?: string;
}

/**
 * Payment Required (HTTP 402) error class.
 *
 * Surfaces the parsed backend paywall envelope as typed fields, read off the
 * normalized `details.paywall` bag that `transformAxiosToApiError` stamps. A
 * bare 402 (no meta) is still a `PaymentRequiredError` with these fields
 * `undefined`. Detection is always by `.status === 402` (see
 * `isPaymentRequiredError`), never by code.
 */
export class PaymentRequiredError extends ApiErrorClass {
  public readonly requiredTier?: SubscriptionTier;
  public readonly currentTier?: SubscriptionTier;
  public readonly upgradePrompt?: UpgradePrompt;
  public readonly feature?: PaywallFeature;
  public readonly upgradeUrl?: string;

  constructor(
    message: string,
    status: number = HTTP_STATUS.PAYMENT_REQUIRED,
    details?: Record<string, unknown>
  ) {
    super(message, API_ERROR_CODES.PAYMENT_REQUIRED, status, details);

    this.name = 'PaymentRequiredError';

    const paywall = (details?.paywall ?? {}) as PaywallDetails;
    this.requiredTier = paywall.requiredTier;
    this.currentTier = paywall.currentTier;
    this.upgradePrompt = paywall.upgradePrompt;
    this.feature = paywall.feature;
    this.upgradeUrl = paywall.upgradeUrl;

    Object.setPrototypeOf(this, PaymentRequiredError.prototype);
  }
}

/**
 * What the other party changed, arriving on a `RESOURCE_STALE` refusal.
 *
 * Pre-formatted display strings, composed for whoever is authenticated — the
 * same shape and the same renderer as a proposal's own diff.
 */
export interface StaleDetails {
  summary: string;
  changes: { label: string; before: string; after: string }[];
}

/**
 * You acted on a version that has since moved.
 *
 * Distinct from a plain conflict because it carries **what moved**, so the
 * caller can re-decide rather than only retry. Read off the normalized
 * `details.drift` bag that `transformAxiosToApiError` stamps.
 *
 * Detection is by **code**, not status — unlike `PaymentRequiredError`, whose
 * 402 identifies it uniquely. A 409 is ambiguous: on change proposals alone it
 * also answers a second pending proposal, an unanswerable one and an already
 * sent reminder, so the status cannot tell them apart.
 */
export class ResourceStaleError extends ApiErrorClass {
  public readonly drift?: StaleDetails;

  constructor(
    message: string,
    status: number = HTTP_STATUS.CONFLICT,
    details?: Record<string, unknown>
  ) {
    super(message, API_ERROR_CODES.RESOURCE_STALE, status, details);

    this.name = 'ResourceStaleError';
    this.drift = details?.drift as StaleDetails | undefined;

    Object.setPrototypeOf(this, ResourceStaleError.prototype);
  }
}

// =============================================================================
// CONFIRMATION REQUIRED (423) TYPES
// =============================================================================

/**
 * What confirming takes, arriving on a `CONFIRMATION_REQUIRED` refusal as
 * `meta.methods` and `meta.two_factor`. Normalized onto `details.confirmation`.
 */
export interface ConfirmationDetails {
  methods: ConfirmationMethod[];
  twoFactor: boolean;
}

/**
 * The action needs the owner to confirm it's them, recently (HTTP 423).
 *
 * Connecting or disconnecting a provider, a first password, asking to change
 * the email and setting two-factor up answer this until the session or bearer
 * has confirmed in the last ten minutes. It is not an authentication failure:
 * the session is fine, so nothing refreshes or signs out. Confirm with one of
 * `confirmation.methods`, then retry the request. Read off the normalized
 * `details.confirmation` bag that `transformAxiosToApiError` stamps.
 */
export class ConfirmationRequiredError extends ApiErrorClass {
  public readonly confirmation?: ConfirmationDetails;

  constructor(
    message: string,
    status: number = HTTP_STATUS.LOCKED,
    details?: Record<string, unknown>
  ) {
    super(message, API_ERROR_CODES.CONFIRMATION_REQUIRED, status, details);

    this.name = 'ConfirmationRequiredError';
    this.confirmation = details?.confirmation as
      ConfirmationDetails | undefined;

    Object.setPrototypeOf(this, ConfirmationRequiredError.prototype);
  }
}

/**
 * The session that sent a request ended before its response arrived.
 *
 * Not a refusal from the server: the request may well have succeeded. It is
 * the API client declining to hand the response to a caller who is now in a
 * different account's session (or none), so nothing of the old account can be
 * written into a store or a cache from here. Raised on every request, after
 * the fact, by comparing the session epoch captured when the request left with
 * the one current when it settled (docs/roadmap/session-scoped-state.md, D5).
 *
 * Callers should treat it as silence, not failure: the session that would
 * have shown the result, or the error, is gone. `cause` carries the original
 * rejection when the request itself failed.
 */
export class SessionEndedError extends ApiErrorClass {
  constructor(
    details?: Record<string, unknown>,
    options?: { cause?: unknown }
  ) {
    super(
      'The session ended before the request finished',
      API_ERROR_CODES.SESSION_ENDED,
      undefined,
      details
    );
    this.name = 'SessionEndedError';
    if (options?.cause !== undefined) this.cause = options.cause;
    Object.setPrototypeOf(this, SessionEndedError.prototype);
  }
}

/**
 * Network Error class
 */
export class NetworkError extends ApiErrorClass {
  constructor(
    message: string,
    code: ApiErrorCode = API_ERROR_CODES.NETWORK_ERROR,
    details?: Record<string, unknown>
  ) {
    super(message, code, undefined, details);
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Server Error class
 */
export class ServerError extends ApiErrorClass {
  constructor(
    message: string,
    code: ApiErrorCode = API_ERROR_CODES.SERVER_ERROR,
    status: number = HTTP_STATUS.INTERNAL_SERVER_ERROR,
    details?: Record<string, unknown>
  ) {
    super(message, code, status, details);
    this.name = 'ServerError';
    Object.setPrototypeOf(this, ServerError.prototype);
  }
}

// =============================================================================
// ERROR FACTORY AND UTILITIES
// =============================================================================

/**
 * A 422's field errors as `ValidationErrorClass` reads them: messages keyed by
 * field. The response transform parses the API's `errors` into a flat list of
 * `{ field, message }` (`details.validationErrors`), and a caller that builds
 * the error itself may pass the keyed record already.
 */
function fieldErrorsFrom(value: unknown): Record<string, string[]> {
  if (Array.isArray(value)) {
    const byField: Record<string, string[]> = {};
    for (const entry of value) {
      const { field, message } = (entry ?? {}) as Partial<ValidationErrorInfo>;
      if (typeof field === 'string' && typeof message === 'string') {
        byField[field] = [...(byField[field] ?? []), message];
      }
    }
    return byField;
  }

  return value && typeof value === 'object'
    ? (value as Record<string, string[]>)
    : {};
}

/**
 * Create appropriate error instance based on error data
 */
export function createApiError(
  message: string,
  code: ApiErrorCode = API_ERROR_CODES.UNKNOWN_ERROR,
  status?: number,
  details?: Record<string, unknown>
): ApiErrorClass {
  // Paywall (402) is code-keyed, not category-keyed: it shares the
  // AUTHORIZATION category with FORBIDDEN, so branch on the code first.
  if (code === API_ERROR_CODES.PAYMENT_REQUIRED) {
    return new PaymentRequiredError(message, status, details);
  }

  // Staleness is code-keyed for a sharper reason than the paywall's: it shares
  // both its category and its 409 status with RESOURCE_CONFLICT, so nothing
  // but the code distinguishes them.
  if (code === API_ERROR_CODES.RESOURCE_STALE) {
    return new ResourceStaleError(message, status, details);
  }

  // Confirming it's you is code-keyed like the paywall: it shares the
  // AUTHORIZATION category with FORBIDDEN.
  if (code === API_ERROR_CODES.CONFIRMATION_REQUIRED) {
    return new ConfirmationRequiredError(message, status, details);
  }

  const typeInfo = ERROR_TYPE_MAP[code];

  switch (typeInfo.category) {
    case ErrorCategory.AUTHENTICATION:
      return new AuthError(message, code, details);

    case ErrorCategory.VALIDATION:
      return new ValidationErrorClass(
        message,
        fieldErrorsFrom(details?.validationErrors),
        details
      );

    case ErrorCategory.NETWORK:
    case ErrorCategory.TIMEOUT:
      return new NetworkError(message, code, details);

    case ErrorCategory.SERVER:
      return new ServerError(message, code, status, details);

    default:
      return new ApiErrorClass(message, code, status, details);
  }
}

/**
 * Extract error code from HTTP status
 */
export function getErrorCodeFromStatus(status: number): ApiErrorCode {
  switch (status) {
    case HTTP_STATUS.BAD_REQUEST:
      return API_ERROR_CODES.BAD_REQUEST;
    case HTTP_STATUS.UNAUTHORIZED:
      return API_ERROR_CODES.UNAUTHENTICATED;
    case HTTP_STATUS.FORBIDDEN:
      return API_ERROR_CODES.FORBIDDEN;
    case HTTP_STATUS.PAYMENT_REQUIRED:
      return API_ERROR_CODES.PAYMENT_REQUIRED;
    case HTTP_STATUS.NOT_FOUND:
      return API_ERROR_CODES.NOT_FOUND;
    case HTTP_STATUS.CONFLICT:
      return API_ERROR_CODES.RESOURCE_CONFLICT;
    case HTTP_STATUS.UNPROCESSABLE_ENTITY:
      return API_ERROR_CODES.VALIDATION_ERROR;
    case HTTP_STATUS.LOCKED:
      return API_ERROR_CODES.CONFIRMATION_REQUIRED;
    case HTTP_STATUS.INTERNAL_SERVER_ERROR:
      return API_ERROR_CODES.SERVER_ERROR;
    case HTTP_STATUS.BAD_GATEWAY:
      return API_ERROR_CODES.BAD_GATEWAY;
    case HTTP_STATUS.SERVICE_UNAVAILABLE:
      return API_ERROR_CODES.SERVICE_UNAVAILABLE;
    case 429: // Too Many Requests
      return API_ERROR_CODES.RATE_LIMITED;
    default:
      return API_ERROR_CODES.UNKNOWN_ERROR;
  }
}

/**
 * Check if error is retryable based on code or category
 */
export function isRetryableError(error: ApiErrorClass | ApiError): boolean {
  if (error instanceof ApiErrorClass) {
    return error.retryable;
  }

  const typeInfo = ERROR_TYPE_MAP[error.code as ApiErrorCode];
  return typeInfo?.retryable || false;
}

/**
 * Check if error should be reported
 */
export function shouldReportError(error: ApiErrorClass | ApiError): boolean {
  if (error instanceof ApiErrorClass) {
    return error.reportable;
  }

  const typeInfo = ERROR_TYPE_MAP[error.code as ApiErrorCode];
  return typeInfo?.reportable || false;
}

/**
 * Get user-friendly message for error
 */
export function getUserErrorMessage(error: ApiErrorClass | ApiError): string {
  if (error instanceof ApiErrorClass) {
    return error.getUserMessage();
  }

  const typeInfo = ERROR_TYPE_MAP[error.code as ApiErrorCode];
  return typeInfo?.userMessage || error.message;
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Check if error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    error !== null &&
    typeof error === 'object' &&
    'code' in error &&
    'message' in error &&
    'timestamp' in error
  );
}

/**
 * Check if error is an authentication error
 */
export function isAuthenticationError(error: unknown): error is AuthError {
  return (
    isApiError(error) &&
    (error.code.includes('AUTH') ||
      error.code === API_ERROR_CODES.UNAUTHENTICATED)
  );
}

/**
 * Check if error is a validation error
 */
export function isValidationError(
  error: unknown
): error is ValidationErrorClass {
  return isApiError(error) && error.code === API_ERROR_CODES.VALIDATION_ERROR;
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): error is NetworkError {
  if (!isApiError(error)) return false;

  const networkCodes = [
    API_ERROR_CODES.NETWORK_ERROR,
    API_ERROR_CODES.TIMEOUT_ERROR,
    API_ERROR_CODES.CONNECTION_ERROR,
    API_ERROR_CODES.DNS_ERROR,
    API_ERROR_CODES.SSL_ERROR,
  ];

  return networkCodes.includes(error.code as (typeof networkCodes)[number]);
}

/**
 * Check if error is a paywall (HTTP 402) error.
 *
 * Detection is always by `.status === 402`, never by code: a 402 shares the
 * AUTHORIZATION category (and would share a code namespace) with plain
 * authorization failures, so the status is the only reliable discriminator.
 * Any `ApiErrorClass` carrying a 402 status is treated as a paywall — the
 * request coordinator hands every transformed 402 through as a
 * `PaymentRequiredError`, but this guard is intentionally structural so a
 * hand-built `ApiErrorClass` with status 402 still classifies.
 */
export function isPaymentRequiredError(
  error: unknown
): error is PaymentRequiredError {
  return (
    error instanceof ApiErrorClass &&
    error.status === HTTP_STATUS.PAYMENT_REQUIRED
  );
}

/**
 * Whether a request was refused because the session is an admin's read-only
 * look at a member's account. By code: the status, 403, is every refusal's.
 */
export function isReadOnlyError(error: unknown): error is ApiErrorClass {
  return (
    error instanceof ApiErrorClass && error.code === API_ERROR_CODES.READ_ONLY
  );
}

/**
 * Whether an error is a stale read carrying what moved.
 *
 * By code rather than status, because a 409 alone cannot say which refusal it
 * is — see {@link ResourceStaleError}.
 */
export function isResourceStaleError(
  error: unknown
): error is ResourceStaleError {
  return (
    error instanceof ApiErrorClass &&
    error.code === API_ERROR_CODES.RESOURCE_STALE
  );
}

/**
 * Whether an error asks the owner to confirm it's them before retrying — see
 * {@link ConfirmationRequiredError}. By code, which a bare 423 maps to as well.
 */
export function isConfirmationRequiredError(
  error: unknown
): error is ConfirmationRequiredError {
  return (
    error instanceof ApiErrorClass &&
    error.code === API_ERROR_CODES.CONFIRMATION_REQUIRED
  );
}

/**
 * The request outlived the session that sent it — see {@link SessionEndedError}.
 * A caller that catches one has nothing to show: the session that would have
 * shown it is over.
 */
export function isSessionEndedError(
  error: unknown
): error is SessionEndedError {
  return (
    error instanceof ApiErrorClass &&
    error.code === API_ERROR_CODES.SESSION_ENDED
  );
}

/**
 * Check if error is a server error
 */
export function isServerError(error: unknown): error is ServerError {
  if (!isApiError(error)) return false;

  const serverCodes = [
    API_ERROR_CODES.SERVER_ERROR,
    API_ERROR_CODES.SERVICE_UNAVAILABLE,
    API_ERROR_CODES.BAD_GATEWAY,
    API_ERROR_CODES.GATEWAY_TIMEOUT,
    API_ERROR_CODES.DATABASE_ERROR,
    API_ERROR_CODES.THIRD_PARTY_SERVICE_ERROR,
  ];

  return serverCodes.includes(error.code as (typeof serverCodes)[number]);
}

// =============================================================================
// ERROR REPORTING INTERFACES
// =============================================================================

/**
 * Error report structure for analytics and monitoring
 */
export interface ErrorReport {
  error: ApiError;
  context: {
    userId?: string;
    sessionId?: string;
    userAgent?: string;
    url?: string;
    timestamp: string;
    stack?: string;
  };
  metadata?: Record<string, unknown>;
}

/**
 * Error analytics event
 */
export interface ErrorAnalyticsEvent {
  type: 'error';
  category: ErrorCategory;
  severity: ErrorSeverity;
  code: string;
  message: string;
  retryable: boolean;
  context: Record<string, unknown>;
  timestamp: string;
}

// =============================================================================
// EXPORT ALL TYPES AND UTILITIES
// =============================================================================

// All types are already exported inline above - no need for separate export block
